import { CommonModule } from '@angular/common';
import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { LayoutSection, PTypes } from '@digital-platform/rs-cms/domain';
import { BasePTypeComponent } from '../base-ptype/base-ptype.component';
import { DynamicContentWrapperComponent } from '../dynamic-content-wrapper/dynamic-content-wrapper.component';

@Component({
  selector: 'digital-platform-layout-section',
  standalone: true,
  imports: [CommonModule, DynamicContentWrapperComponent],
  templateUrl: './layout-section.component.html',
  styleUrls: ['./layout-section.component.scss'],
})
export class LayoutSectionComponent
  extends BasePTypeComponent
  implements LayoutSection, OnChanges
{
  public override type = 'layoutSection' as const;
  @Input()
  public override behaviorSettings: {
    layout_paragraphs: {
      layout: string;
      config: { layout: { classes: {} }; [propName: string]: any };
    };
  } = {
    layout_paragraphs: {
      layout: '',
      config: {
        layout: {
          classes: {},
        },
      },
    },
  };

  @Input()
  public children: PTypes[] = [];

  // keep track of the regions as we use it
  public childrenByRegion: Map<string, PTypes[]> = new Map();

  public layoutTypeClassMap: { [key: string]: string } = {
    layout_image: 'layout-col-1',
    layout_onecol: 'layout-col-1',
    layout_one_column: 'layout-col-1',
    'layout-bs-1col': 'layout-col-1',
    bs_1col: 'layout-col-1',

    layout_twocol: 'layout-col-2',
    layout_two_column: 'layout-col-2',
    layout_twocol_section: 'layout-col-2',
    bs_2col: 'layout-col-2',

    layout_threecol: 'layout-col-3',
    layout_three_column: 'layout-col-3',
    'layout-bs-3col': 'layout-col-3',
    bs_3col: 'layout-col-3',
    layout_threecol_section: 'layout-col-3',
    layout_threecol_33_34_33: 'layout-col-3',

    layout_fourcol: 'layout-col-4',
    layout_four_column: 'layout-col-4',
    'layout-bs-4col': 'layout-col-4',
    bs_4col: 'layout-col-4',

    layout_fourcol_stacked: 'layout-col-4-stacked',
    'layout-bs-4col-stacked': 'layout-col-4',
  };

  public originalOrder = (): number => 0;

  public get isBootstrapLayout(): boolean {
    return (
      this.behaviorSettings?.layout_paragraphs?.layout?.startsWith('bs') &&
      this.behaviorSettings.layout_paragraphs.layout !== 'bs_1col'
    );
  }

  public get layoutClass() {
    const classes = [''];
    if (!Array.isArray(this.behaviorSettings)) {
      const layout = this.behaviorSettings.layout_paragraphs.layout;
      if (layout) {
        classes.push('layout');
        if (this.layoutTypeClassMap[layout]) {
          classes.push(this.layoutTypeClassMap[layout]);
        }
      }
    }
    return classes.join(' ');
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.children) {
      for (const child of this.children) {
        const regionName: string =
          child.behaviorSettings.layout_paragraphs.region;
        if (!this.childrenByRegion.has(regionName)) {
          this.childrenByRegion.set(regionName, []);
        }
        this.childrenByRegion.get(regionName)?.push(child);
      }
    }
  }

  get parentClasses(): string {
    const classList: [string] = [''];
    const parentClasses =
      this.behaviorSettings?.layout_paragraphs?.config?.layout?.classes ?? {};
    for (const parentClass in parentClasses) {
      if (Object.prototype.hasOwnProperty.call(parentClasses, parentClass)) {
        classList.push(parentClass);
      }
    }
    return classList.join(' ');
  }

  /**
   * Custom child classes which need to applied based on the region, a child element belongs
   */
  public getCustomClasses(regionName: string): string {
    const classList: [string] = ['bs-child'];
    const layoutConfig: { [key: string]: { classes: any } } =
      this.behaviorSettings?.layout_paragraphs?.config ?? {};
    if (layoutConfig[regionName]) {
      for (const className of Object.keys(
        layoutConfig[regionName]?.classes ?? {}
      )) {
        classList.push(className);
      }
    }
    return classList.join(' ');
  }
}
